import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { AUTHORIZED_ACTIONS, USER_RIGHTS } from '../../consts';
import messages from '../../intl/messages';
import { getUserRole } from '../../store/auth/selectors';
import { fetchDashboardExams, fetchDefinitions, fetchServicesVersions } from '../../store/model/actions';
import { getInstructionsByType, getServicesVersions } from '../../store/model/selectors';
import checkUserAuthorization from '../../utils/userRights';
import ExamFilter from './ExamFilter';
import PostponedExams from './PostponedExams';
import ReviewedExams from './ReviewedExams';

const useStyles = makeStyles()((theme) => ({
	bounding: {
		padding: '1.5rem',
		'& .MuiAlert-root': {
			marginBottom: '20px',
		},
	},
}));

const VersionRow = ({ message, version, prefix = '' }) => (
	<div>
		<FormattedMessage {...message} />
		{': '}
		{version ? (
			<strong>{prefix}{version}</strong>
		) : (
			<span style={{ color: '#9e9e9e' }}>n/a</span>
		)}
	</div>
);

const Dashboard = () => {
	const { classes } = useStyles();
	const dispatch = useDispatch();
	const userRole = useSelector(getUserRole());
	const instructions = useSelector(getInstructionsByType);
	const servicesVersions = useSelector(getServicesVersions());

	const canReviewExams = checkUserAuthorization(userRole, [
		AUTHORIZED_ACTIONS.REVIEW_AS_REVIEWER,
		AUTHORIZED_ACTIONS.REVIEW_AS_COMMISSIONER,
	]);

	useEffect(() => {
		dispatch(fetchDefinitions());
		dispatch(fetchServicesVersions());
	}, [dispatch]);

	useEffect(() => {
		if (canReviewExams) dispatch(fetchDashboardExams());
	}, [canReviewExams, dispatch]);

	return (
		<div className={classes.bounding}>
			{instructions?.[userRole]?.text && (
				<Alert severity="warning">
					<strong>
						<FormattedMessage {...messages.instructions} />{' '}
					</strong>
					<span dangerouslySetInnerHTML={{ __html: instructions[userRole].text }} />
				</Alert>
			)}
			{canReviewExams && (
				<>
					<PostponedExams />
					<br />
					<ReviewedExams />
				</>
			)}
			{USER_RIGHTS[userRole].includes(AUTHORIZED_ACTIONS.VIEW_EXAM_FILTER) && (
				<>
					<br /> <ExamFilter />
				</>
			)}
			<Paper elevation={1} style={{ padding: '12px 16px', marginTop: '20px', display: 'inline-block' }}>
				<VersionRow
					message={messages.desktopAppVersion}
					version={servicesVersions?.desktop?.version}
					prefix="v"
				/>
				<VersionRow
					message={messages.proctoringAdminVersion}
					version={process.env.REACT_APP_GIT_REF}
				/>
				<VersionRow
					message={messages.sciolinkApiVersion}
					version={servicesVersions?.sciolink?.version}
				/>
				<VersionRow
					message={messages.adminApiVersion}
					version={servicesVersions?.admin?.version}
				/>
				<VersionRow
					message={messages.workerVersion}
					version={servicesVersions?.worker?.version}
				/>
			</Paper>
		</div>
	);
};

export default Dashboard;
